import React, { createContext, useState, useEffect, useContext } from "react";
import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { serverTimestamp } from "firebase/firestore";

const UserContext = createContext({ user: null, userData: null });

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);     // Firebase auth user
    const [userData, setUserData] = useState(null); // Firestore document data
    const auth = getAuth();

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                // Clear user and userData immediately when logged out
                setUser(null);
                setUserData(null);
                
                // Then try to sign in anonymously
                try {
                    const userCredential = await signInAnonymously(auth);
                    console.log("Signed in anonymously", userCredential.user);
                    // The onAuthStateChanged listener will trigger again with the new anonymous user
                } catch (error) {
                    console.error("Anonymous sign-in failed", error);
                }
                return;
            }
            
            // User is authenticated (either normally or anonymously)
            setUser(currentUser);
            
            try {
                const userRef = doc(db, "users", currentUser.uid);
                const docSnap = await getDoc(userRef);

                if (!docSnap.exists()) {
                    // Define default data for all users
                    const defaultUserData = {
                        username: currentUser.isAnonymous ? "Guest" : "User",
                        profilePic: currentUser.isAnonymous ? "profile1" : "profile2",
                        elo: 1000,
                        isAnonymous: currentUser.isAnonymous,
                        createdAt: serverTimestamp(),
                        // Add any other default fields here
                    };

                    // Create the document with default values
                    await setDoc(userRef, defaultUserData);
                    setUserData({ ...defaultUserData, uid: currentUser.uid });
                } else {
                    // Document exists—check if we need to add default values
                    const userData = docSnap.data();
                    const updates = {};
                    let needsUpdate = false;
                    
                    // Check if default fields exist, if not add them
                    if (!userData.profilePic) {
                        updates.profilePic = "profile2";
                        needsUpdate = true;
                    }
                    
                    if (!userData.elo) {
                        updates.elo = 1000;
                        needsUpdate = true;
                    }
                    
                    if (!userData.createdAt) {
                        updates.createdAt = serverTimestamp();
                        needsUpdate = true;
                    }
                    
                    // Update the document if needed
                    if (needsUpdate) {
                        await updateDoc(userRef, updates);
                        // Update local state with the new values
                        setUserData({ ...userData, ...updates, uid: currentUser.uid });
                    } else {
                        // Set userData from Firestore
                        setUserData({ ...userData, uid: currentUser.uid });
                    }
                }
            } catch (error) {
                console.error("Error accessing Firestore:", error);
                // Still set the user even if Firestore fails
                setUserData({ uid: currentUser.uid, isAnonymous: currentUser.isAnonymous });
            }
        });

        return () => unsubscribeAuth();
    }, []); // Added dependency array to prevent re-running effect unnecessarily


    return (
        <UserContext.Provider value={{ user, userData, setUserData }}>
            {children}
        </UserContext.Provider>
    );
};